import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { Loader2, Unlock, RefreshCw } from 'lucide-react';
import { AvatarDisplay } from '../../utils/avatars';
import { useModal } from '../../context/ModalContext';

export default function AdminBlocked() {
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showConfirm, showAlert } = useModal();

  const fetchBlocks = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('blocked_users')
        .select(`
          blocker_id,
          blocked_id,
          created_at,
          blocker:profiles!blocked_users_blocker_id_fkey(id, name, avatar),
          blocked:profiles!blocked_users_blocked_id_fkey(id, name, avatar)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBlocks(data || []);
    } catch (err) {
      console.error('Fetch blocks error:', err);
      showAlert('錯誤', '無法載入封鎖列表');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const handleUnblock = async (blockerId, blockedId, blockerName, blockedName) => {
    showConfirm('解除封鎖', `確定要解除「${blockerName}」對「${blockedName}」的封鎖嗎？`, async () => {
      try {
        const { error } = await supabase
          .from('blocked_users')
          .delete()
          .eq('blocker_id', blockerId)
          .eq('blocked_id', blockedId);

        if (error) throw error;
        setBlocks(prev => prev.filter(b => !(b.blocker_id === blockerId && b.blocked_id === blockedId)));
      } catch (err) {
        console.error('Unblock error:', err);
        showAlert('錯誤', '解除封鎖失敗');
      }
    });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>封鎖管理</h2>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '4px' }}>
            共 {blocks.length} 筆封鎖紀錄
          </p>
        </div>
        <button
          onClick={fetchBlocks}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px',
            background: '#f1f5f9', border: 'none', borderRadius: '10px',
            color: '#475569', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer',
          }}
        >
          <RefreshCw size={15} />
          重新整理
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#94a3b8' }} />
        </div>
      ) : blocks.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: 'white', borderRadius: '16px',
          border: '1px solid #e2e8f0', color: '#94a3b8',
        }}>
          目前沒有任何封鎖紀錄
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {blocks.map((block) => (
            <div
              key={`${block.blocker_id}-${block.blocked_id}`}
              style={{
                background: 'white',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              {/* Blocker */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '160px' }}>
                <AvatarDisplay avatarId={block.blocker?.avatar} size={40} />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#1e293b' }}>
                    {block.blocker?.name || '未知用戶'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>封鎖方</div>
                </div>
              </div>

              {/* Arrow */}
              <div style={{
                padding: '6px 12px',
                background: '#fef2f2', borderRadius: '8px',
                fontSize: '0.8rem', fontWeight: '700', color: '#ef4444',
                whiteSpace: 'nowrap',
              }}>
                封鎖了 →
              </div>

              {/* Blocked */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '160px' }}>
                <AvatarDisplay avatarId={block.blocked?.avatar} size={40} />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#1e293b' }}>
                    {block.blocked?.name || '未知用戶'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>被封鎖方</div>
                </div>
              </div>

              {/* Date */}
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {new Date(block.created_at).toLocaleDateString('zh-TW')}
              </div>

              {/* Unblock button */}
              <button
                onClick={() => handleUnblock(
                  block.blocker_id, block.blocked_id,
                  block.blocker?.name || '未知', block.blocked?.name || '未知'
                )}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px',
                  background: '#f6f9fc', border: '1px solid #bfdbfe',
                  borderRadius: '10px', color: '#4e8bca',
                  fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <Unlock size={14} />
                解除封鎖
              </button>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
